import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { atelierService, userService, apprenantService } from '../services/api';
import { toast } from 'sonner';
import { Plus, X, Users, User, UserPlus, Briefcase } from 'lucide-react';
import api from '../services/api';

const AddAtelierDialog = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [animateurs, setAnimateurs] = useState([]);
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [invitableUsers, setInvitableUsers] = useState([]); // Financeurs, Référents, autres Intervenants
  const [selectedAnimateurs, setSelectedAnimateurs] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [selectedInvites, setSelectedInvites] = useState([]);
  
  const [formData, setFormData] = useState({
    titre: '',
    type_atelier: 'atelier_thematique',
    date: new Date().toISOString().split('T')[0],
    heure_debut: '09:00',
    heure_fin: '12:00',
    zone_geographique: 'Saint-Pierre',
    lieu: '',
    description: ''
  });

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    try {
      const [animateursRes, beneficiairesRes, invitablesRes] = await Promise.all([
        userService.getAnimateurs(),
        apprenantService.getAll(),
        api.get('/users/invitables')
      ]);
      
      setAnimateurs(animateursRes.data);
      setBeneficiaires(beneficiairesRes.data);
      setInvitableUsers(invitablesRes.data || []);
    } catch (error) {
      toast.error('Erreur de chargement des données');
    }
  };

  const handleToggleAnimateur = (animateurId) => {
    setSelectedAnimateurs(prev => 
      prev.includes(animateurId) 
        ? prev.filter(id => id !== animateurId)
        : [...prev, animateurId]
    );
  };

  const handleToggleParticipant = (participantId) => {
    setSelectedParticipants(prev => 
      prev.includes(participantId) 
        ? prev.filter(id => id !== participantId)
        : [...prev, participantId]
    );
  };

  const handleSelectAllParticipants = () => {
    if (selectedParticipants.length === beneficiaires.length) {
      setSelectedParticipants([]);
    } else {
      setSelectedParticipants(beneficiaires.map(b => b.id));
    }
  };

  const handleToggleInvite = (userId) => {
    setSelectedInvites(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'financeur': return 'bg-purple-100 text-purple-700';
      case 'referent': return 'bg-green-100 text-green-700';
      case 'animateur': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getRoleLabel = (role) => {
    switch(role) {
      case 'financeur': return 'Financeur';
      case 'referent': return 'Référent';
      case 'animateur': return 'Intervenant';
      default: return role;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedAnimateurs.length === 0) {
      toast.error('Veuillez sélectionner au moins un intervenant');
      return;
    }
    
    setLoading(true);

    try {
      await atelierService.create({
        ...formData,
        animateurs_ids: selectedAnimateurs,
        participants_ids: selectedParticipants,
        invites_ids: selectedInvites
      });
      
      toast.success('Atelier créé avec succès');
      setOpen(false);
      resetForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de création');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      titre: '',
      type_atelier: 'atelier_thematique',
      date: new Date().toISOString().split('T')[0],
      heure_debut: '09:00',
      heure_fin: '12:00',
      zone_geographique: 'Saint-Pierre',
      lieu: '',
      description: ''
    });
    setSelectedAnimateurs([]);
    setSelectedParticipants([]);
    setSelectedInvites([]);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-atelier-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">
          <Plus className="w-4 h-4 mr-2" />
          Nouvel Atelier
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Nunito, sans-serif' }}>Nouvel Atelier / Événement Collectif</DialogTitle>
          <DialogDescription>Créez un atelier, job dating ou autre événement collectif</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="titre">Titre *</Label>
              <Input
                id="titre"
                data-testid="atelier-titre-input"
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                required
                className="rounded-xl"
                placeholder="Ex: Atelier codes entreprise"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type d'événement *</Label>
              <Select value={formData.type_atelier} onValueChange={(val) => setFormData({ ...formData, type_atelier: val })}>
                <SelectTrigger data-testid="atelier-type-select" className="rounded-xl">
                  <SelectValue placeholder="Sélectionner un type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="atelier_thematique">Atelier thématique</SelectItem>
                  <SelectItem value="job_dating_inverse">Job dating inversé</SelectItem>
                  <SelectItem value="cafe_emploi">Café de l'emploi</SelectItem>
                  <SelectItem value="visite_entreprise">Visite d'entreprise</SelectItem>
                  <SelectItem value="rencontre_metiers">Rencontre métiers en tension</SelectItem>
                  <SelectItem value="comite_suivi">Comité de suivi</SelectItem>
                  <SelectItem value="jury">Jury</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                data-testid="atelier-date-input"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="heure_debut">Heure début</Label>
              <Input
                id="heure_debut"
                type="time"
                value={formData.heure_debut}
                onChange={(e) => setFormData({ ...formData, heure_debut: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="heure_fin">Heure fin</Label>
              <Input
                id="heure_fin"
                type="time"
                value={formData.heure_fin}
                onChange={(e) => setFormData({ ...formData, heure_fin: e.target.value })}
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="zone">Zone Géographique</Label>
            <Select value={formData.zone_geographique} onValueChange={(val) => setFormData({ ...formData, zone_geographique: val })}>
              <SelectTrigger data-testid="atelier-zone-select" className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Saint-Pierre">Saint-Pierre</SelectItem>
                <SelectItem value="Sainte-Marie">Sainte-Marie</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="lieu">Lieu</Label>
            <Input
              id="lieu"
              value={formData.lieu}
              onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
              className="rounded-xl"
              placeholder="Ex: Salle de réunion, 12 rue des Acacias..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Informations complémentaires</Label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              placeholder="Programme, documents à apporter, consignes particulières..."
            />
          </div>

          {/* Sélection des intervenants */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Intervenant(s) * <span className="text-xs text-red-500">(au moins un requis)</span>
            </Label>
            <div className="border rounded-xl p-3 max-h-[150px] overflow-y-auto bg-slate-50">
              {animateurs.length === 0 ? (
                <p className="text-sm text-slate-500">Aucun intervenant disponible</p>
              ) : (
                <div className="space-y-2">
                  {animateurs.map((animateur) => (
                    <div
                      key={animateur.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                        selectedAnimateurs.includes(animateur.id) 
                          ? 'bg-blue-100 border border-blue-300' 
                          : 'hover:bg-slate-100'
                      }`}
                      onClick={() => handleToggleAnimateur(animateur.id)}
                    >
                      <input 
                        type="checkbox"
                        checked={selectedAnimateurs.includes(animateur.id)}
                        onChange={() => handleToggleAnimateur(animateur.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="font-medium">{animateur.prenom} {animateur.nom}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedAnimateurs.length > 0 && (
              <p className="text-xs text-blue-600">{selectedAnimateurs.length} intervenant(s) sélectionné(s)</p>
            )}
          </div>

          {/* Sélection des participants */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Participants
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSelectAllParticipants}
                className="text-xs rounded-lg"
              >
                {selectedParticipants.length === beneficiaires.length ? 'Désélectionner tout' : 'Sélectionner tout'}
              </Button>
            </div>
            <div className="border rounded-xl p-3 max-h-[200px] overflow-y-auto bg-slate-50">
              {beneficiaires.length === 0 ? (
                <p className="text-sm text-slate-500">Aucun bénéficiaire disponible</p>
              ) : (
                <div className="space-y-2">
                  {beneficiaires.map((beneficiaire) => (
                    <div
                      key={beneficiaire.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                        selectedParticipants.includes(beneficiaire.id) 
                          ? 'bg-emerald-100 border border-emerald-300' 
                          : 'hover:bg-slate-100'
                      }`}
                      onClick={() => handleToggleParticipant(beneficiaire.id)}
                    >
                      <input 
                        type="checkbox"
                        checked={selectedParticipants.includes(beneficiaire.id)}
                        onChange={() => handleToggleParticipant(beneficiaire.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="font-medium">{beneficiaire.prenom} {beneficiaire.nom}</span>
                      <span className="text-xs text-slate-500">{beneficiaire.zone_geographique}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedParticipants.length > 0 && (
              <p className="text-xs text-emerald-600">{selectedParticipants.length} participant(s) sélectionné(s)</p>
            )}
          </div>

          {/* Sélection des invités (Financeurs, Référents — hors intervenants déjà sélectionnés) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Invités externes <span className="text-xs text-slate-500">(Financeurs, Référents)</span>
            </Label>
            <div className="border rounded-xl p-3 max-h-[180px] overflow-y-auto bg-slate-50">
              {/* Exclure les animateurs déjà listés dans la section Intervenant(s) */}
              {invitableUsers.filter(u => !animateurs.some(a => a.id === u.id)).length === 0 ? (
                <p className="text-sm text-slate-500">Aucun utilisateur disponible</p>
              ) : (
                <div className="space-y-2">
                  {invitableUsers.filter(u => !animateurs.some(a => a.id === u.id)).map((user) => (
                    <div
                      key={user.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                        selectedInvites.includes(user.id) 
                          ? 'bg-purple-100 border border-purple-300' 
                          : 'hover:bg-slate-100'
                      }`}
                      onClick={() => handleToggleInvite(user.id)}
                      data-testid={`invite-user-${user.id}`}
                    >
                      <input 
                        type="checkbox"
                        checked={selectedInvites.includes(user.id)}
                        onChange={() => handleToggleInvite(user.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                      />
                      <div className="flex items-center gap-2 flex-1">
                        <span className="font-medium">{user.prenom} {user.nom}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedInvites.length > 0 && (
              <p className="text-xs text-purple-600">{selectedInvites.length} invité(s) sélectionné(s) - Ils recevront une notification</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button 
              type="submit" 
              data-testid="atelier-submit-button" 
              disabled={loading || selectedAnimateurs.length === 0} 
              className="rounded-xl bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Création...' : 'Créer l\'atelier'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddAtelierDialog;
